/**
 * Modifier-group pricing rules, shared by the top-level customize list and the
 * nested (follow-up) groups.
 *
 * A group carries the same rules wherever it appears — duplicate quantities,
 * tiered prices, "1st FREE" — so a group attached under a modifier must behave
 * exactly as it does at the top of the item. Keeping the maths in one place is
 * what makes that true; when this lived only inside VariationModal the nested
 * copy silently dropped every duplicate rule.
 */

/**
 * Resolve the price of a single Nth unit under an INCREMENTAL tier rule.
 * Each tier is relative to the modifier's base price:
 *   { mode: 'SAME' }              → basePrice
 *   { mode: 'FREE' }              → 0
 *   { mode: 'PLUS', extra: 0.50 } → basePrice + extra
 *   { mode: 'MINUS', extra: 0.50 }→ basePrice - extra (never below 0)
 * Backward compat: a bare number is that unit's absolute price (legacy);
 * a missing tier falls back to SAME (basePrice).
 */
export function resolveTierPrice(tier, basePrice) {
  if (tier === undefined || tier === null) return basePrice;
  if (typeof tier === "number") return tier;
  if (typeof tier === "string") {
    const n = parseFloat(tier);
    return Number.isNaN(n) ? basePrice : n;
  }
  const mode = tier.mode;
  if (mode === "FREE") return 0;
  if (mode === "PLUS") return basePrice + (parseFloat(tier.extra) || 0);
  if (mode === "MINUS") return Math.max(0, basePrice - (parseFloat(tier.extra) || 0));
  return basePrice; // SAME
}

/**
 * Total price for a duplicate-quantity option under the group's pricing mode.
 * - SAME: every unit costs the same (basePrice * qty)
 * - FIRST_FREE_THEN_CHARGED: 1st unit is free, subsequent units at basePrice
 * - INCREMENTAL / CUSTOM: sum of each unit's tier price (relative to base)
 * - null/undefined: defaults to SAME
 */
export function getDuplicateTotal(basePrice, qty, group) {
  if (qty <= 0) return 0;
  // Same guard the server applies (getModifierDuplicateTotal in order.js): with
  // duplicates off, a leftover tier map must not change the price. Keeping the
  // two in step is what makes shown == charged.
  if (group && group.allowDuplicates === false) return basePrice * qty;
  const mode = group?.duplicatePricingMode;

  if (mode === "FIRST_FREE_THEN_CHARGED") {
    return basePrice * Math.max(0, qty - 1);
  }

  if ((mode === "INCREMENTAL" || mode === "CUSTOM") && group?.duplicatePrices) {
    const tiers =
      typeof group.duplicatePrices === "string"
        ? JSON.parse(group.duplicatePrices)
        : group.duplicatePrices;
    let total = 0;
    for (let i = 1; i <= qty; i++) {
      total += resolveTierPrice(tiers[String(i)], basePrice);
    }
    return total;
  }

  return basePrice * qty;
}

/**
 * What the NEXT unit will cost, as a label. Marginal rather than static
 * (total(qty+1) - total(qty)), so the customer sees "1st FREE" / "2nd FREE" /
 * the real surcharge ("+$0.50") instead of a base price the group never
 * charges. An option that is simply free shows no price at all (null).
 */
export function nextUnitPriceLabel(basePrice, qty, group, currencySymbol) {
  const nextUnitPrice =
    getDuplicateTotal(basePrice, qty + 1, group) - getDuplicateTotal(basePrice, qty, group);
  if (nextUnitPrice > 0) return `+${currencySymbol}${nextUnitPrice.toFixed(2)}`;
  if (!(basePrice > 0)) return null;
  const n = qty + 1;
  const ord = `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
  return `${ord} FREE`;
}

/**
 * "Choose up to 2" — the same wording the top-level groups use. Whether the
 * group is optional goes in the Optional/Required tag beside it.
 */
export function chooseSubtitle(min, max) {
  if (min === 0 && (!max || max >= 99)) return "Choose any";
  if (max && min === max) return `Choose ${min}`;
  if (max === 1) return "Choose 1";
  if (min === 0) return `Choose up to ${max}`;
  return `Choose ${min}–${max}`;
}
